import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { IftaLabelModule } from 'primeng/iftalabel';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { Subscription, finalize, switchMap, timer } from 'rxjs';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../services/session-storage.service';
import { PermissionService } from '../../security/permission.service';
import { ShipmentBulkUploadService } from './shipment-bulk-upload.service';
import { ImportBatch, ImportCandidate, ImportCatalog, ImportEdit, ImportField, ImportGroup, ImportGroupSummary, ImportIssue, ImportJob, ImportMatch, ImportOutcome, ImportReview, ImportRow, ImportSelection } from './shipment-bulk-upload.model';

@Component({
  selector: 'app-shipment-bulk-upload', standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, TranslocoModule, ButtonModule, CheckboxModule, DialogModule,
    InputTextModule, InputNumberModule, InputGroupModule, InputGroupAddonModule, IftaLabelModule, ProgressBarModule,
    SelectModule, TableModule, TagModule, ToastModule, PageDataPanelComponent, PageHeroComponent, PageShellComponent],
  templateUrl: './shipment-bulk-upload.component.html', styleUrls: ['./shipment-bulk-upload.component.css']
})
export class ShipmentBulkUploadComponent implements OnInit, OnDestroy {
  catalog?: ImportCatalog;
  batch?: ImportBatch;
  batches: ImportBatch[] = [];
  historyCount = 0;
  historyPage = 0;
  groups: ImportGroupSummary[] = [];
  groupCount = 0;
  page = 0;
  pageSize = 25;
  query = '';
  filter = 'ALL';
  file?: File;
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Riyadh';
  loading = false;
  loadingGroups = false;
  saving = false;
  dragging = false;
  error = '';
  job?: ImportJob;
  selected = new Map<number, ImportGroupSummary>();
  allReady = false;
  active?: ImportGroup;
  dirty = false;
  activeSheet = 'Shipments';
  inspectorTab = 'MATCH';
  fieldSearch = '';
  showNewFields = false;
  review?: ImportReview;
  reviewVisible = false;
  acknowledged = false;
  outcome?: ImportOutcome;
  matching?: ImportMatch;
  matchVisible = false;
  matchQuery = '';
  candidates: ImportCandidate[] = [];
  candidate?: ImportCandidate;
  createNew = false;
  applyEquivalent = true;
  equivalentGroups: ImportGroupSummary[] = [];
  equivalentCount = 0;
  bulkVisible = false;
  bulkSheet = 'Shipments';
  bulkField?: string;
  bulkValue = '';
  bulkPreview = false;
  helpVisible = false;
  private polling?: Subscription;
  private requests = new Subscription();
  private groupLoad = 0;
  private referenceLists: Record<string, {value:string; label:string}[]> = {};
  readonly steps = ['upload', 'match', 'correct', 'review', 'import', 'results'];
  readonly outcomes: ImportOutcome[] = ['READY_FOR_DISPATCH', 'DISPATCH_LIKE_EXPRESS', 'HISTORICAL'];
  readonly states = ['ALL', 'ATTENTION', 'ERRORS', 'MATCHES', 'WARNINGS', 'DRAFT', 'READY', 'IMPORTED', 'FAILED'];
  constructor(
    private service: ShipmentBulkUploadService, private session: SessionStorageService, private messages: MessageService,
    private i18n: TranslocoService, public permissions: PermissionService, private route: ActivatedRoute, private router: Router
  ) {}
  ngOnInit(): void {
    const company = this.session.getCompany()?.id;
    if (!company) { this.error = this.t('companyRequired'); return; }
    this.requests.add(this.service.catalog(company).subscribe({ next: value => this.catalog = value, error: e => this.fail(e) }));
    this.loadHistory();
    const id = Number(this.route.snapshot.queryParamMap.get('batch'));
    if (id > 0) this.openBatch(id);
  }
  ngOnDestroy(): void { this.polling?.unsubscribe(); this.requests.unsubscribe(); }
  t(key: string, params?: Record<string, any>): string { return this.i18n.translate('shipmentImport.' + key, params); }
  outcomeOptions() { return this.outcomes.filter(o => o !== 'HISTORICAL' || this.permissions.hasPermission('shipments.import.historical')).map(value => ({ value, label: this.t('outcomes.' + value) })); }
  stateOptions() { return this.states.map(value => ({ value, label: this.t('states.' + value) })); }
  sheetOptions() { return (this.catalog?.sheets || []).map(s => ({ value: s.name, label: this.t('sheets.' + s.name) })); }
  count(state: string): number { return this.batch?.counts?.[state as keyof ImportBatch['counts']] || 0; }
  get total(): number { return Object.values(this.batch?.counts || {}).reduce((sum, value) => sum + (value || 0), 0); }
  get busy(): boolean { return this.loading || this.saving || !!this.job && ['QUEUED', 'PROCESSING'].includes(this.job.status); }
  get readOnly(): boolean { return !this.active || ['IMPORTED', 'QUEUED', 'PROCESSING'].includes(this.active.state) || this.busy; }
  get selectionCount(): number { return this.allReady ? this.count('READY') : this.selected.size; }
  get progress(): number { return this.job?.total ? Math.round(this.job.completed / this.job.total * 100) : 0; }
  get step(): number {
    if (!this.batch) return 0;
    if (this.job?.phase === 'IMPORT' && this.busy) return 4;
    if (this.total && this.count('IMPORTED') === this.total) return 5;
    if (this.reviewVisible) return 3;
    if (this.count('NEEDS_REVIEW')) return 1;
    return 2;
  }
  choose(event: Event): void { const input = event.target as HTMLInputElement; this.setFile(input.files?.[0]); input.value = ''; }
  drop(event: DragEvent): void { event.preventDefault(); this.dragging = false; this.setFile(event.dataTransfer?.files?.[0]); }
  private setFile(file?: File): void {
    if (!file) return;
    if (!/\.(xlsx|xls)$/i.test(file.name) || file.size > 25 * 1024 * 1024) { this.error = this.t('fileLimit'); return; }
    this.file = file; this.error = '';
  }
  upload(): void {
    if (this.dirty) { this.error = this.t('saveFirst'); return; }
    const company = this.session.getCompany()?.id; if (!company || !this.file || this.busy) return;
    this.loading = true; this.error = '';
    this.requests.add(this.service.upload(this.file, company, this.timezone).pipe(finalize(() => this.loading = false)).subscribe({
      next: batch => { this.resetWorkspace(); this.batch = batch; this.outcome = batch.defaultOutcome; this.setUrl(batch.batchId); this.loadGroups(); this.loadHistory(); this.watch(batch.latestJob); },
      error: e => this.fail(e)
    }));
  }
  loadHistory(page = this.historyPage): void {
    const company = this.session.getCompany()?.id; if (!company) return;
    this.historyPage = page;
    this.requests.add(this.service.batches(company, page).subscribe({ next: p => { this.batches = p.items; this.historyCount = p.count; }, error: e => this.fail(e) }));
  }
  openBatch(id: number): void {
    if (this.dirty) { this.error = this.t('saveFirst'); return; }
    this.loading = true; this.resetWorkspace();
    this.requests.add(this.service.batch(id).pipe(finalize(() => this.loading = false)).subscribe({
      next: batch => { this.batch = batch; this.outcome = batch.defaultOutcome; this.timezone = batch.timezone; this.setUrl(id); this.loadGroups(); this.watch(batch.latestJob); },
      error: e => this.fail(e)
    }));
  }
  private resetWorkspace(): void {
    this.polling?.unsubscribe(); this.selected.clear(); this.allReady = false; this.active = undefined; this.dirty = false;
    this.review = undefined; this.reviewVisible = false; this.page = 0; this.job = undefined; this.error = '';
  }
  private setUrl(id: number): void { void this.router.navigate([], { relativeTo: this.route, queryParams: { batch: id }, queryParamsHandling: 'merge', replaceUrl: true }); }
  loadGroups(loaded?: () => void): void {
    if (!this.batch) return;
    this.loadingGroups = true; const sequence = ++this.groupLoad; const id = this.batch.batchId;
    this.requests.add(this.service.groups(id, this.page, this.pageSize, this.query, this.filter).subscribe({
      next: p => { if (sequence !== this.groupLoad || this.batch?.batchId !== id) return; this.groups = p.items; this.groupCount = p.count; this.loadingGroups = false; loaded?.(); },
      error: e => { this.loadingGroups = false; this.fail(e); }
    }));
  }
  changeFilter(filter?: string): void { if (filter) this.filter = filter; this.page = 0; this.loadGroups(); }
  paginate(event: { first?: number; rows?: number | null }): void { this.pageSize = event.rows || 25; this.page = Math.floor((event.first || 0) / this.pageSize); this.loadGroups(); }
  toggle(group: ImportGroupSummary, checked: boolean): void { checked ? this.selected.set(group.groupId, group) : this.selected.delete(group.groupId); this.allReady = false; this.invalidateReview(); }
  selectPage(): void { this.groups.filter(g => !['IMPORTED','QUEUED','PROCESSING'].includes(g.state)).forEach(g => this.selected.set(g.groupId, g)); this.allReady = false; this.invalidateReview(); }
  clearSelection(): void { this.selected.clear(); this.allReady = false; this.invalidateReview(); }
  inspect(group: ImportGroupSummary): void {
    if (this.dirty) { this.error = this.t('saveFirst'); return; }
    const batchId = this.batch?.batchId;
    this.requests.add(this.service.group(group.groupId).subscribe({ next: value => { if (this.batch?.batchId !== batchId) return; this.active = value; this.inspectorTab = value.issues.length ? 'ISSUES' : 'MATCH'; this.activeSheet = 'Shipments'; this.fieldSearch = ''; }, error: e => this.fail(e) }));
  }
  inspectorRows(): ImportRow[] { return this.active?.rows.filter(r => r.sheet === this.activeSheet) || []; }
  fields(row: ImportRow): ImportField[] {
    return (this.catalog?.sheets.find(s => s.name === row.sheet)?.fields || []).filter(f => {
      if (!this.showNewFields && f.key.startsWith('new')) return false;
      const q = this.fieldSearch.toLowerCase(); return !q || f.key.toLowerCase().includes(q) || this.fieldLabel(f).toLowerCase().includes(q);
    });
  }
  fieldLabel(field: ImportField): string {
    const key = field.key.split('.').pop()!; const path = 'shipmentImport.fields.' + key;
    const translated = this.i18n.translate(path); return translated === path ? field.label : translated;
  }
  fieldOptions(field: ImportField) { return (field.type === 'boolean' ? ['true', 'false'] : field.options).map(value => ({ value, label: this.enumLabel(value) })); }
  referenceType(field: ImportField): string | undefined {
    const key = field.key.split('.').pop() || '';
    if (/countryId$/i.test(key)) return 'COUNTRY';
    if (/cityId$/i.test(key)) return 'CITY';
    return ({ vehicleTypeId: 'VEHICLE_TYPE', vehicleConfigId: 'VEHICLE_CONFIG', vehicleCapacityId: 'VEHICLE_CAPACITY' } as Record<string,string>)[key];
  }
  referenceOptions(field: ImportField, row: ImportRow): {value:string;label:string}[] {
    const type = this.referenceType(field); const company = this.batch?.companyId || this.session.getCompany()?.id;
    if (!type || !company) return [];
    const key = field.key.split('.').pop()!; const prefix = field.key.slice(0, field.key.length - key.length);
    const parent = type === 'CITY' ? row.values[prefix + key.replace(/cityId$/i,'CountryId').replace(/^CountryId$/,'countryId')] : type.startsWith('VEHICLE_') && type !== 'VEHICLE_TYPE' ? row.values[prefix + 'vehicleTypeId'] : undefined;
    if ((type === 'CITY' || ['VEHICLE_CONFIG','VEHICLE_CAPACITY'].includes(type)) && !parent) return [];
    const cacheKey = company + ':' + type + ':' + (parent || '');
    if (!this.referenceLists[cacheKey]) {
      this.referenceLists[cacheKey] = [];
      this.requests.add(this.service.referenceOptions(company, type, parent).subscribe({ next: options => this.referenceLists[cacheKey] = options, error: e => this.fail(e) }));
    }
    return this.referenceLists[cacheKey];
  }
  enumLabel(value: string): string { const key = 'shipmentImport.values.' + value; const label = this.i18n.translate(key); return label === key ? value.replaceAll('_', ' ') : label; }
  numeric(value?: string): number | null { return value && Number.isFinite(Number(value)) ? Number(value) : null; }
  dateInput(value?: string): string {
    if (!value) return '';
    if (!/(Z|[+-]\d{2}:\d{2})$/i.test(value)) return value.length === 10 ? value + 'T00:00' : value.replace(' ', 'T');
    const date = new Date(value); if (!Number.isFinite(date.getTime())) return value;
    const parts = new Intl.DateTimeFormat('sv-SE', { timeZone: this.batch?.timezone || this.timezone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' }).formatToParts(date);
    const get = (key: string) => parts.find(p => p.type === key)?.value || '';
    return get('year') + '-' + get('month') + '-' + get('day') + 'T' + get('hour') + ':' + get('minute') + ':' + get('second');
  }
  setNumber(row: ImportRow, field: string, value: number | null): void { row.values[field] = value == null ? '' : String(value); this.changed(); }
  changed(): void { this.dirty = true; this.invalidateReview(); }
  fieldIssues(row: ImportRow, key: string): ImportIssue[] { return this.active?.issues.filter(i => i.sheet === row.sheet && i.row === row.rowNumber && i.field === key) || []; }
  saveActive(): void {
    if (!this.active || !this.batch || this.readOnly) return;
    const g = this.active; this.saving = true;
    const edit: ImportEdit = { groupIds: [g.groupId], revisions: { [g.groupId]: g.revision }, rows: g.rows, outcome: g.outcomeOverride, clearOutcome: !g.outcomeOverride };
    this.requests.add(this.service.edit(this.batch.batchId, edit).pipe(finalize(() => this.saving = false)).subscribe({
      next: () => { this.dirty = false; this.validate([g.groupId]); }, error: e => this.fail(e)
    }));
  }
  discard(): void { if (!this.active) return; this.dirty = false; this.requests.add(this.service.group(this.active.groupId).subscribe({ next: value => this.active = value, error: e => this.fail(e) })); }
  focusIssue(issue: ImportIssue): void { this.activeSheet = issue.sheet; this.inspectorTab = 'DETAILS'; this.fieldSearch = issue.field; this.showNewFields = issue.field.startsWith('new'); }
  nextIssue(): void {
    if (this.dirty) { this.error = this.t('saveFirst'); return; }
    if (this.loadingGroups) return;
    const current = this.active?.groupId; const candidates = this.groups.filter(g => g.errors || g.unmatched || g.state === 'FAILED');
    const index = candidates.findIndex(g => g.groupId === current);
    if (index + 1 < candidates.length) { this.inspect(candidates[index + 1]); return; }
    this.page = this.filter === 'ATTENTION' && (this.page + 1) * this.pageSize < this.groupCount ? this.page + 1 : 0;
    this.filter = 'ATTENTION';
    this.loadGroups(() => { if (this.groups.length) this.inspect(this.groups[0]); });
  }
  saveDefaults(): void {
    if (!this.batch || this.busy || this.dirty) return; this.saving = true; this.invalidateReview();
    this.requests.add(this.service.defaults(this.batch.batchId, this.outcome).pipe(finalize(() => this.saving = false)).subscribe({
      next: b => { this.batch = b; this.validate(); }, error: e => this.fail(e)
    }));
  }
  validate(ids?: number[]): void {
    if (!this.batch || this.dirty) return; this.invalidateReview();
    this.requests.add(this.service.validate(this.batch.batchId, { groupIds: ids || [], allReady: false }).subscribe({ next: job => this.watch(job), error: e => this.fail(e) }));
  }
  private watch(job?: ImportJob): void {
    this.polling?.unsubscribe(); this.job = job;
    if (!job || !['QUEUED', 'PROCESSING'].includes(job.status)) return;
    this.polling = timer(1000, 2000).pipe(switchMap(() => this.service.job(job.jobId))).subscribe({
      next: value => {
        this.job = value;
        if (!['QUEUED', 'PROCESSING'].includes(value.status)) {
          this.polling?.unsubscribe(); this.refresh();
          if (value.status === 'FAILED') this.error = value.error || this.t('jobFailed');
        }
      }, error: e => { this.polling?.unsubscribe(); this.fail(e); }
    });
  }
  resume(): void { if (this.job) this.requests.add(this.service.resume(this.job.jobId).subscribe({ next: j => this.watch(j), error: e => this.fail(e) })); }
  private refresh(): void {
    if (!this.batch) return;
    const id = this.batch.batchId;
    this.requests.add(this.service.batch(id).subscribe({ next: batch => { if (this.batch?.batchId !== id) return; this.batch = batch; this.job = batch.latestJob; this.loadGroups(); }, error: e => this.fail(e) }));
    if (this.active && !this.dirty) this.requests.add(this.service.group(this.active.groupId).subscribe({ next: g => this.active = g, error: e => this.fail(e) }));
    this.clearSelection(); this.loadHistory();
  }
  openMatch(match: ImportMatch): void {
    if (this.dirty) { this.error = this.t('saveFirst'); return; }
    this.matching = match; this.matchQuery = match.input; this.candidates = match.candidates;
    this.candidate = match.candidates.find(c => c.id === match.selectedId); this.createNew = false; this.applyEquivalent = true; this.matchVisible = true;
    this.equivalentGroups = []; this.equivalentCount = 0;
    if (this.active) this.requests.add(this.service.decisionPreview(this.active.groupId, match).subscribe({ next: result => { if (this.matching !== match) return; this.equivalentGroups = result.items; this.equivalentCount = result.count; }, error: e => this.fail(e) }));
  }
  searchMatches(): void {
    if (!this.active || !this.matching) return;
    this.requests.add(this.service.candidates(this.active.groupId, this.matching, this.matchQuery).subscribe({ next: candidates => { this.candidates = candidates; this.candidate = undefined; }, error: e => this.fail(e) }));
  }
  canCreate(match?: ImportMatch): boolean {
    if (!match) return false;
    const permissions: Record<string, string> = { CLIENT: 'crm.clients.create', LOCATION: 'location.records.create', CONTACT: 'customer.records.create', CRM_CONTACT: 'customer.records.create', DRIVER: 'fleet.drivers.create', VEHICLE: 'fleet.vehicles.create', WORKER: 'operations.fieldwork.manage' };
    return !!permissions[match.kind] && this.permissions.hasPermission(permissions[match.kind]);
  }
  confirmMatch(): void {
    if (!this.active || !this.matching || (!this.createNew && !this.candidate)) return;
    const groupId = this.active.groupId; const match = this.matching; const create = this.createNew; this.saving = true;
    this.requests.add(this.service.decide(groupId, match, this.createNew ? undefined : this.candidate, this.createNew, this.applyEquivalent).pipe(finalize(() => this.saving = false)).subscribe({
      next: response => {
        this.matchVisible = false; this.invalidateReview();
        this.messages.add({ severity: 'success', summary: this.t('decisionSaved'), detail: this.t('affectedGroups', { count: response.affectedGroups }) });
        if (create) { this.requests.add(this.service.group(groupId).subscribe({ next: g => { this.active = g; this.activeSheet = match.rowKey.split(':')[0]; this.showNewFields = true; this.fieldSearch = this.draftPrefix(match); this.inspectorTab = 'DETAILS'; }, error: e => this.fail(e) })); this.loadGroups(); }
        else this.validate();
      }, error: e => this.fail(e)
    }));
  }
  draftPrefix(match: ImportMatch): string {
    const map: Record<string, string> = { client: 'newClient', clientContact: 'newContactForClient', pickup: 'newSourceLocation', dropoff: 'newDestinationLocation', pickupContact: 'newSourceContact', dropoffContact: 'newDestinationContact', driver: 'newDriver', vehicle: 'newVehicle', location: 'newLocation', contact: 'newContact', worker: 'newLaborer' };
    return map[match.field] || 'new';
  }
  openBulk(): void { this.bulkPreview = false; this.bulkField = undefined; this.bulkValue = ''; this.bulkVisible = true; }
  bulkFields() { return (this.catalog?.sheets.find(s => s.name === this.bulkSheet)?.fields || []).filter(f => !['shipmentRef', 'deliveryRef', 'lineNumber'].includes(f.key)).map(f => ({ value: f.key, label: this.fieldLabel(f) })); }
  bulkDefinition(): ImportField | undefined { return this.catalog?.sheets.find(s => s.name === this.bulkSheet)?.fields.find(f => f.key === this.bulkField); }
  applyBulk(): void {
    if (!this.batch || !this.bulkField || !this.selected.size || !this.bulkPreview) return;
    const revisions: Record<number, number> = {}; this.selected.forEach(g => revisions[g.groupId] = g.revision);
    this.saving = true; this.requests.add(this.service.edit(this.batch.batchId, { groupIds: [...this.selected.keys()], revisions, sheet: this.bulkSheet, values: { [this.bulkField]: this.bulkValue } }).pipe(finalize(() => this.saving = false)).subscribe({
      next: () => { this.bulkVisible = false; this.dirty = false; this.validate(); }, error: e => this.fail(e)
    }));
  }
  private selection(): ImportSelection { return { groupIds: [...this.selected.keys()], allReady: this.allReady }; }
  openReview(): void {
    if (!this.batch || !this.selectionCount || this.dirty || this.busy) return;
    this.loading = true; this.requests.add(this.service.review(this.batch.batchId, this.selection()).pipe(finalize(() => this.loading = false)).subscribe({
      next: r => { this.review = r; this.acknowledged = false; this.reviewVisible = true; }, error: e => this.fail(e)
    }));
  }
  confirmImport(): void {
    if (!this.batch || !this.review || this.review.warnings.length && !this.acknowledged) return;
    this.saving = true; this.requests.add(this.service.importReviewed(this.batch.batchId, this.review.token, this.acknowledged).pipe(finalize(() => this.saving = false)).subscribe({
      next: job => { this.reviewVisible = false; this.watch(job); }, error: e => this.fail(e)
    }));
  }
  private invalidateReview(): void { this.review = undefined; }
  downloadTemplate(): void { const company = this.session.getCompany()?.id; if (company) this.requests.add(this.service.template(company).subscribe({ next: b => this.download(b, 'shipment-workbench-template.xlsx'), error: e => this.fail(e) })); }
  export(failedOnly = false): void { if (this.batch) this.requests.add(this.service.export(this.batch.batchId, failedOnly).subscribe({ next: b => this.download(b, 'shipment-import-' + this.batch!.batchId + (failedOnly ? '-unresolved' : '-results') + '.xlsx'), error: e => this.fail(e) })); }
  private download(blob: Blob, name: string): void { const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url); }
  severity(state: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' { return state === 'IMPORTED' ? 'success' : state === 'READY' ? 'info' : ['INVALID', 'FAILED'].includes(state) ? 'danger' : state === 'NEEDS_REVIEW' ? 'warn' : 'secondary'; }
  private fail(error: any): void { this.error = error?.error?.detail || error?.error?.message || error?.message || this.t('requestFailed'); }
}
